import { useState, useCallback } from 'react';
import { CalendarEvent, CalendarEventSource } from '../../../../../types/calendar';
import { calendarApi } from '../../../../../services/calendarApi';

type ViewMode = 'month' | 'week' | 'day';

const formatLocalDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// workspaceId is optional - when not provided, fetches all events (no filter)
// sources: which event sources to union (PRD §5.1); defaults to
// legislative+workspace inside a workspace, legislative-only otherwise.
export const useCalendarSimple = (workspaceId?: string, myEvents?: boolean, sources?: CalendarEventSource[]) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Simple fetch function without complex dependencies
  const fetchCalendarData = useCallback(async (date: Date, viewMode: ViewMode) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Calculate date range based on view mode
      let startDate: string;
      let endDate: string;
      
      // Rolling windows: anchor date + N days
      if (viewMode === 'month') {
        // 30-day rolling window starting from anchor date
        startDate = formatLocalDate(date);
        const end = new Date(date);
        end.setDate(date.getDate() + 29);
        endDate = formatLocalDate(end);
      } else if (viewMode === 'week') {
        // 7-day rolling window starting from anchor date
        startDate = formatLocalDate(date);
        const end = new Date(date);
        end.setDate(date.getDate() + 6);
        endDate = formatLocalDate(end);
      } else {
        // 1-day view
        startDate = formatLocalDate(date);
        endDate = startDate;
      }
      
      // Only pass workspace_id if provided (otherwise fetch all events)
      // When no workspace and myEvents is true, filter to user's tracked bills
      const response = await calendarApi.getEvents({
        start_date: startDate,
        end_date: endDate,
        workspace_id: workspaceId || undefined,
        my_events: (!workspaceId && myEvents) ? 'true' : undefined,
        sources: sources && sources.length > 0
          ? sources.join(',')
          : workspaceId ? 'legislative,workspace' : undefined,
        limit: 1000
      });
      if (response.success) {
        setEvents(response.data.events || []);
      } else {
        console.error('Calendar API failed:', response);
        setError('Failed to load calendar data');
      }
    } catch (err: any) {
      console.error('Error fetching calendar data:', err);
      setError(err.message || 'Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, myEvents, sources]);

  // Get events for a specific date
  const getEventsForDate = useCallback((dateStr: string) => {
    return events.filter(event => {
      // Handle both date-only strings and full ISO timestamps
      const eventDate = event.reading_date ? event.reading_date.split('T')[0] : null;
      return eventDate === dateStr;
    });
  }, [events]);

  // Get calendar summary
  const getCalendarSummary = useCallback(() => {
    const summary = {
      total_bills: 0,
      senate_chamber_hearings: 0,
      senate_committee_hearings: 0,
      house_chamber_hearings: 0,
      house_committee_hearings: 0,
      total_events: events.length
    };

    events.forEach(event => {
      // Count hearings by chamber and type
      const chamber = event.chamber?.toLowerCase();
      const isCommittee = event.reading_title?.toLowerCase().includes('committee');

      if (chamber === 'senate') {
        if (isCommittee) {
          summary.senate_committee_hearings++;
        } else {
          summary.senate_chamber_hearings++;
        }
      } else if (chamber === 'house') {
        if (isCommittee) {
          summary.house_committee_hearings++;
        } else {
          summary.house_chamber_hearings++;
        }
      }
    });

    return summary;
  }, [events]);

  return {
    events,
    isLoading,
    error,
    fetchCalendarData,
    getEventsForDate,
    getCalendarSummary,
    refetch: () => {} // Simple stub
  };
};
